import { useCallback, useEffect, useMemo, useState } from "react"

import { getCurrencyComboboxOptions } from "@/lib/app-currency"
import type { SearchableComboboxOption } from "@/components/combobox-with-states-4"
import { usdPerUnitFromUsdBaseRates, type UsdPerUnitMap } from "@/lib/currency-convert"
import { frankfurterLatestFromUsdUrl } from "@/lib/frankfurter-url"
import { supabase } from "@/lib/supabase"

const STALE_MS = 36 * 60 * 60 * 1000

type RateRow = {
  base_currency: string
  quote_currency: string
  rate: number
  updated_at: string
}

async function fetchFrankfurterUsdRatesFull(): Promise<{ rates: Record<string, number>; asOf: string }> {
  const res = await fetch(frankfurterLatestFromUsdUrl())
  if (!res.ok) throw new Error(`Frankfurter HTTP ${res.status}`)
  const body = (await res.json()) as { date?: string; rates?: Record<string, number> }
  const rates = body.rates ?? {}
  return { rates, asOf: body.date ?? new Date().toISOString().slice(0, 10) }
}

function rowsToUsdPerUnit(rows: RateRow[]): UsdPerUnitMap | null {
  const fromQuotes = rows.filter((r) => r.base_currency === "USD" && r.quote_currency !== "USD")
  if (fromQuotes.length === 0) return null
  const rates: Record<string, number> = {}
  for (const r of fromQuotes) {
    rates[r.quote_currency] = r.rate
  }
  return usdPerUnitFromUsdBaseRates(rates)
}

export type UseCurrencyExchangeRatesResult = {
  usdPerUnit: UsdPerUnitMap | null
  /** Each option has `value` (ISO code), `label` / `filterText` for search, and `flag` + `name` for display. */
  currencyOptions: SearchableComboboxOption[]
  asOfLabel: string | null
  source: "database" | "live" | "none"
  error: string | null
  loading: boolean
  refresh: () => Promise<void>
}

export function useCurrencyExchangeRates(): UseCurrencyExchangeRatesResult {
  const [usdPerUnit, setUsdPerUnit] = useState<UsdPerUnitMap | null>(null)
  const [asOfLabel, setAsOfLabel] = useState<string | null>(null)
  const [source, setSource] = useState<"database" | "live" | "none">("none")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const currencyOptions = useMemo(() => {
    if (!usdPerUnit || Object.keys(usdPerUnit).length === 0) return []
    return getCurrencyComboboxOptions(usdPerUnit)
  }, [usdPerUnit])

  const refresh = useCallback(async () => {
    setError(null)
    setLoading(true)
    let staleFallback: { map: UsdPerUnitMap; asOf: string } | null = null

    try {
      const { data, error: qErr } = await supabase
        .from("currency_exchange_rates")
        .select("base_currency, quote_currency, rate, updated_at")
        .eq("base_currency", "USD")

      if (!qErr && data && data.length > 0) {
        const rows = data as RateRow[]
        const newest = rows.reduce((a, b) => (a > b.updated_at ? a : b.updated_at), rows[0]!.updated_at)
        const age = Date.now() - new Date(newest).getTime()
        const map = rowsToUsdPerUnit(rows)
        if (map && age <= STALE_MS) {
          setUsdPerUnit(map)
          setAsOfLabel(new Date(newest).toISOString().slice(0, 10))
          setSource("database")
          setLoading(false)
          return
        }
        if (map && age > STALE_MS) {
          staleFallback = { map, asOf: new Date(newest).toISOString().slice(0, 10) }
        }
      }

      const { rates, asOf } = await fetchFrankfurterUsdRatesFull()
      const map = usdPerUnitFromUsdBaseRates(rates)
      setUsdPerUnit(map)
      setAsOfLabel(asOf)
      setSource("live")
    } catch (e) {
      if (staleFallback) {
        setUsdPerUnit(staleFallback.map)
        setAsOfLabel(staleFallback.asOf)
        setSource("database")
        setError(null)
      } else {
        setUsdPerUnit(null)
        setAsOfLabel(null)
        setSource("none")
        setError(e instanceof Error ? e.message : "Could not load exchange rates.")
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { usdPerUnit, currencyOptions, asOfLabel, source, error, loading, refresh }
}
